import {
  FEEDBACK_SCREENSHOT_ACCEPT,
  MAX_FEEDBACK_SCREENSHOT_BYTES,
} from "@estelle/domain";

/**
 * Reading an image the reporter brought themselves (#498), from any of the
 * three ways they can: the clipboard, a drop, or the file picker. The dialog
 * treats what comes out exactly like the automatic capture — it goes through
 * the same canvas, can be blacked out the same way, and is stored under the
 * same limits.
 */

const ACCEPTED = FEEDBACK_SCREENSHOT_ACCEPT.split(",");

/**
 * Read off the input rather than refused: a camera photo or a retina screenshot
 * is routinely over the stored limit as a PNG and well under it once re-encoded
 * as a JPEG, which is what the dialog does with the capture too. This only
 * stops a file so large that decoding it would stall the tab.
 */
const MAX_INPUT_BYTES = 10 * 1024 * 1024;

/** What is said of anything that is not a PNG or a JPEG, whatever stopped it. */
export const NOT_AN_IMAGE = "Only a PNG or JPEG image can be attached.";

/**
 * Whether the browser can actually read `file` as an image.
 *
 * A file's `type` is the browser's guess from its name, so a text file called
 * `x.png` arrives as `image/png` and passes `imageProblem`. Nothing then
 * decoded it until the canvas tried, which failed without a word and left the
 * previous picture standing — the dialog said "Choose a different image" over
 * the automatic screenshot, and that is what was sent (#523). Decoding it here,
 * before it replaces anything, is what lets a bad one be refused in the same
 * words as any other non-image.
 */
export async function canDecode(file: Blob): Promise<boolean> {
  const url = URL.createObjectURL(file);
  try {
    const image = new Image();
    image.src = url;
    await image.decode();
    return true;
  } catch {
    return false;
  } finally {
    URL.revokeObjectURL(url);
  }
}

/** What is wrong with a chosen file, or `null` when it can be used. */
export function imageProblem(file: File): string | null {
  if (!ACCEPTED.includes(file.type)) {
    return NOT_AN_IMAGE;
  }
  if (file.size === 0) return "That image is empty.";
  if (file.size > MAX_INPUT_BYTES) {
    return `That image is over ${String(MAX_INPUT_BYTES / 1024 / 1024)} MB. Crop or shrink it first.`;
  }
  return null;
}

/** Said when even the JPEG re-encode of a chosen image will not fit. */
export const STILL_TOO_LARGE = `That image is still over ${String(MAX_FEEDBACK_SCREENSHOT_BYTES / 1024 / 1024)} MB after compressing. Choose a smaller one.`;

/**
 * The first image a clipboard or a drag carries, or `null`.
 *
 * `files` first, then `items`: a screenshot pasted from the clipboard arrives as
 * a file in Chrome and Firefox, and only as an item in Safari. Anything that is
 * not a file — the text of an ordinary paste — is ignored, so the note box
 * keeps working as it always did.
 */
export function imageFrom(data: DataTransfer | null): File | null {
  if (!data) return null;
  for (const file of Array.from(data.files)) {
    if (file.type.startsWith("image/")) return file;
  }
  for (const item of Array.from(data.items)) {
    if (item.kind === "file" && item.type.startsWith("image/")) {
      const file = item.getAsFile();
      if (file) return file;
    }
  }
  return null;
}

/** Whether a drag is carrying files, so the dialog can offer to take them. */
export function isFileDrag(data: DataTransfer | null): boolean {
  return data !== null && Array.from(data.types).includes("Files");
}
